import { Button, Badge, ButtonGroup } from 'reactstrap';
import {
    FundRequestVendor,
    Requests,
    SimpleFundRequest,
} from '~/types/fundrequests';
import TableContainer from '~/containers/Campaign/components/TableContainer';
import { ColumnDef } from '@tanstack/react-table';
import { toUSD, getBadgeLabel, getBadgeClass } from '~/shared/utils';
import { renderIcon } from '~/shared/components/icons';
import { AiOutlineStop, AiOutlineEye } from 'react-icons/ai';
import { useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
    fetchFundRequestVendorDetails,
    fetchFundRequestVendorMIPDetails,
} from '~/services/fundrequests';
import { AdminContext } from '~/context/admin';
import { FundRequestContext } from '~/context/fundrequests';
import moment from 'moment';
import toast from 'react-hot-toast';
import { ChangeRequestModal } from './ChangeRequestModal';
import { AddAddressModal } from './AddAddressModal';

const PayeeDetails = () => {
    const { vendorId } = useParams<{ vendorId: string }>();
    const [{ isAdmin, campaignId }, adminDispatch] = useContext(AdminContext);
    const [{ selectedPayee }, fundRequestDispatch] =
        useContext(FundRequestContext);
    const [mipDetails, setMipDetails] = useState<FundRequestVendor>();
    const history = useHistory();
    const [openModal, setOpenModal] = useState(false);
    const [openAddressModal, setOpenAddressModal] = useState(false);

    const getButtonColor = (fund_request_status: string): string => {
        switch (fund_request_status.toLowerCase()) {
            case 'submitted':
                return 'text-primary';
            case 'needs additional info':
                return 'text-primary';
            case 'draft':
                return 'text-primary';
            default:
                return 'opacity-25';
        }
    };
    const columns: ColumnDef<SimpleFundRequest>[] = [
        {
            id: 'status',
            header: 'Status',
            accessorFn: (props) => {
                const statusLabel = props?.fund_request_status;
                const badgeLabel = statusLabel;

                const getStatusValueFromLabel = (label: string): string => {
                    const labelToValue: { [key: string]: string } = {
                        'Draft': 'draft',
                        'Submitted': 'submitted',
                        'In Review': 'in_review',
                        'Needs Info': 'needs_info',
                        'Resubmitted': 'resubmitted',
                        'Approved': 'approved',
                        'Paid': 'paid',
                        'Rejected': 'rejected'
                    };
                    return labelToValue[label] || label.toLowerCase().replace(/\s+/g, '_');
                };

                const statusValue = getStatusValueFromLabel(statusLabel);
                const badgeClass = getBadgeClass(badgeLabel, statusValue);

                return (
                    <Badge
                        color='none'
                        className={badgeClass}
                    >
                        {badgeLabel}
                    </Badge>
                );
            },
        },
        {
            id: 'created_date',
            header: 'Date',
            accessorFn: (props) => {
                return (
                    <div>{moment(props.created_date).format('MM/DD/YYYY')}</div>
                );
            },
        },

        {
            id: 'amount',
            header: 'Amount',
            accessorFn: (props) => {
                return <span>{toUSD(props.request_amount)}</span>;
            },
        },

        {
            id: 'actions',
            header: 'Actions',
            accessorFn: (props) => {
                const isViewOnly = [
                    'Open',
                    'Submitted',
                    'Approved',
                    'Paid',
                    'Rejected',
                    'Resubmitted',
                    'In Review',
                    'For DOF Approval',
                    'Pending',
                    'AP Review',
                ].includes(props.fund_request_status);

                return (
                    <div className='d-lg-flex'>
                        <Button
                            color='white'
                            className={`p-1 m-0 ${isViewOnly ? 'text-primary' : getButtonColor(
                                props.fund_request_status
                            )}`}
                            style={{ fontSize: '20px' }}
                            onClick={() => {
                                history.push(
                                    `/requests/request/submit/${props?.id}`
                                );
                            }}
                        >
                            {isViewOnly ? <AiOutlineEye /> : renderIcon('edit')}
                        </Button>
                        {/* <Button
                            color='white'
                            className='p-1 m-0'
                            style={{ fontSize: '20px' }}
                            onClick={async () => {}}
                        >
                            {renderIcon('copy')}
                        </Button> */}
                        <Button
                            color='white'
                            className={`p-1 m-0 ${props.fund_request_status === 'Draft'
                                ? 'text-danger'
                                : 'opacity-25'
                                }`}
                            style={{ fontSize: '20px' }}
                            disabled={
                                !['Draft', 'Submitted'].includes(
                                    props.fund_request_status
                                )
                            }
                        >
                            <AiOutlineStop color='red' />
                        </Button>
                    </div>
                );
            },
        },
    ];

    const fetchVendorDetails = async () => {
        try {
            const payeeDetails = await fetchFundRequestVendorDetails(
                parseInt(vendorId),
                campaignId
            );
            if (payeeDetails.detail) {
                history.push('/login');
                return;
            }
            fundRequestDispatch({
                type: 'set_selected_payee',
                payload: payeeDetails,
            });
        } catch (error) {
            console.log(error);
        }
    };

    const fetchMIPDetails = async (vendor_id: string) => {
        try {
            const mipDetails = await fetchFundRequestVendorMIPDetails(
                vendorId,
                vendor_id,
                campaignId
            );
            if (!mipDetails?.error) {
                setMipDetails(mipDetails);
            }
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        fetchVendorDetails();
    }, []);

    useEffect(() => {
        if (selectedPayee?.vendor_id && vendorId) {
            fetchMIPDetails(selectedPayee?.vendor_id);
        }
    }, [selectedPayee, vendorId]);

    const checkValueExists = (value: any) => {
        if (value) return value;

        return '';
    };

    const getFortmatedAddress = (
        city: string,
        state: string,
        zipcode: string
    ) => {
        if (!city && !state && !zipcode) return '';
        return `${city}, ${state} ${zipcode}`;
    };

    const getAddress = (address: any) => {
        return address?.address_1 || address?.address_2 || '';
    };

    return (
        <>
            <div className='container__inner'>
                <h1 className='h2 mb-4'>{selectedPayee?.name}</h1>
                {selectedPayee?.verified_in_mip === true && (
                    <Badge
                        color='none'
                        style={{
                            backgroundColor: '#25b7b5',
                        }}
                    >
                        <p style={{ margin: 0, color: 'black' }}>
                            {'Verified'}
                        </p>
                    </Badge>
                )}

                {selectedPayee?.pending_change_request ? (
                    <div
                        className='d-flex justify-content-start align-items-center p-4 w-100 mt-5'
                        style={{
                            backgroundColor: '#97D7E11A',
                            borderRadius: '16px',
                        }}
                    >
                        <p className='h5 mb-0 fw-bold'>
                            A CHANGE REQUEST FOR THIS PAYEE IS CURRENTLY PENDING
                        </p>
                    </div>
                ) : (
                    <div
                        className='d-flex justify-content-between align-items-center p-4 w-100 mt-5'
                        style={{
                            backgroundColor: '#97D7E11A',
                            borderRadius: '16px',
                        }}
                    >
                        <h3 className='h5 mb-0 fw-bold'>
                            NEED TO MAKE A CHANGE TO THIS PAYEE?
                        </h3>
                        <Button
                            color='primary'
                            size='sm'
                            onClick={() => setOpenModal(true)}
                        >
                            Request Change
                        </Button>
                    </div>
                )}

                <div className='mt-5 d-flex flex-column flex-lg-row justify-content-between'>
                    <div style={{ width: '40%' }}>
                        <div className='mb-4 space-y-1 text-gray-600'>
                            <p className='fs-6 fw-semibold'>
                                {selectedPayee?.name}
                            </p>
                            <p className='mt-4'>{selectedPayee?.phone}</p>
                            <p className='fw-bold text-primary'>
                                {selectedPayee?.email}
                            </p>

                            <div className='mb-3 fw-bold small'>Addresses</div>

                            {selectedPayee?.vendor_addresses
                                ?.sort((a: any, b: any) => {
                                    if (a?.is_primary) return -1;
                                    if (b?.is_primary) return 1;
                                    return 0;
                                })
                                .map((address: any, index: number) => {
                                    return (
                                        <div key={index} className='mb-3'>
                                            <p className='mb-0 fw-bold'>
                                                {address?.address_label}
                                                {address?.is_primary && (
                                                    <span>{' (primary)'}</span>
                                                )}
                                            </p>
                                            <p>
                                                {getAddress(address)}
                                                <br />
                                                {getFortmatedAddress(
                                                    checkValueExists(
                                                        address?.city
                                                    ),
                                                    checkValueExists(
                                                        address?.state
                                                            ?.abbreviation
                                                    ),
                                                    checkValueExists(
                                                        address?.zip_code
                                                    )
                                                )}
                                            </p>
                                        </div>
                                    );
                                })}

                            <Button
                                color='outline-dark'
                                size='sm'
                                onClick={() => setOpenAddressModal(true)}
                            >
                                Add Address
                            </Button>
                        </div>

                        <div className='space-y-3 mb-4'>
                            <p className='fw-bold'>Preferred Payment Method</p>
                            <ButtonGroup>
                                <Button
                                    size='sm'
                                    outline={
                                        selectedPayee?.payment_type !== 'CHECK'
                                    }
                                    className='me-3'
                                >
                                    Check
                                </Button>
                                <Button
                                    size='sm'
                                    outline={
                                        selectedPayee?.payment_type !== 'ACH'
                                    }
                                    disabled
                                >
                                    Direct Deposit (ACH)
                                </Button>
                            </ButtonGroup>
                        </div>

                        {mipDetails?.ach_last_4 && (
                            <div className='bg-white p-4 border rounded-lg w-100'>
                                <h3 className='h5 mb-2'>DIRECT DEPOSIT</h3>
                                <p className='text-gray-600 mb-0'>
                                    Bank ••••{mipDetails?.ach_last_4}{' '}
                                    {mipDetails?.ach_type &&
                                        mipDetails.ach_type[0].toUpperCase() +
                                        mipDetails.ach_type.slice(1)}
                                </p>
                            </div>
                        )}
                    </div>

                    <div style={{ width: '57%' }}>
                        <p className='h4 ms-2 mb-0'>Requests</p>
                        <TableContainer
                            showHeaders
                            columns={columns}
                            data={selectedPayee?.fund_requests}
                            showFilterComponents={false}
                            isServerSidePagination={false}
                            isRequestTable={true}
                        />
                    </div>
                </div>

                <div className='sticky-bottom'>
                    <Button
                        type='submit'
                        color='link'
                        onClick={() => history.push('/payees')}
                    >
                        RETURN TO PAYEES
                    </Button>
                </div>
            </div>
            <ChangeRequestModal
                isOpen={openModal}
                toggle={() => setOpenModal(!openModal)}
                payeeName={selectedPayee?.name}
                onSuccess={() => fetchVendorDetails()}
            />
            <AddAddressModal
                isOpen={openAddressModal}
                toggle={() => setOpenAddressModal(!openAddressModal)}
                setPayeeDetails={(updatedPayee) => {
                    fundRequestDispatch({
                        type: 'set_selected_payee',
                        payload: updatedPayee,
                    });
                }}
            />
        </>
    );
};

export default PayeeDetails;
